// The body `GET /v1/repos/:owner/:repo/policy` serves and MCP `project policy_get`
// carries (#3911): the EFFECTIVE policy — what dispatch, babysit and the agent
// loop actually run — plus why it is not the repo's own when it is not.
//
// Declared here rather than beside `resolvePolicy` (`@developerz/policy`, which
// produces the value) because the two readers cannot import that package:
// `@developerz/mcp` never depends on it, and the dashboard keeps zod + the yaml
// parser out of its bundle. `apps/api` pins the producer to this shape at compile
// time — the route annotates its body with {@link RepoPolicyWire}.
//
// `null` from the port (unknown repo, disabled, no active snapshot) is a 404 and
// never reaches this body; a BROKEN snapshot is a 200 with `fallback` set.

import { z } from 'zod';

/** Why the effective policy is `defaultPolicy` rather than the repo's snapshot. */
export const PolicyFallbackWireSchema = z.object({
  /** `shape`: the snapshot no longer fits the schema. `semantic`: it fits and contradicts itself. */
  stage: z.enum(['shape', 'semantic']),
  /** Every failing path (`["handoff", "webhook_ref"]`) with its message. Never empty. */
  errors: z
    .array(z.object({ path: z.array(z.union([z.string(), z.number()])), message: z.string() }))
    .readonly(),
});

export type PolicyFallbackWire = z.infer<typeof PolicyFallbackWireSchema>;

/**
 * A person's platform-side merge hold on the repo (#7011): "Held for review". The hold
 * only tightens, so `auto_merge` is always the literal `false` here; `policy.pr.auto_merge`
 * in the same body is already the effective value with the hold applied.
 */
export const MergeOverrideWireSchema = z.object({
  auto_merge: z.literal(false),
  /** The user who set it; `null` for a token-only caller. */
  set_by_user_id: z.string().nullable(),
  /** ISO-8601 instant the hold was set. */
  set_at: z.string(),
});

export type MergeOverrideWire = z.infer<typeof MergeOverrideWireSchema>;

export const RepoPolicyWireSchema = z.object({
  /** The effective policy, schema-materialized, tier-gated and with any merge hold applied. */
  policy: z.record(z.string(), z.unknown()),
  /** `null`: `policy` IS the repo's snapshot. Set: `policy` is the platform default. */
  fallback: PolicyFallbackWireSchema.nullable(),
  /**
   * The platform-side merge hold, or `null` when nobody held the repo (#7011). REQUIRED:
   * a body without the field is refused, never read as `null` — a missing field is not
   * evidence that nobody held the repo.
   */
  merge_override: MergeOverrideWireSchema.nullable(),
});

export type RepoPolicyWire = z.infer<typeof RepoPolicyWireSchema>;
